import { CheckCircle, Devices, Refresh } from '@mui/icons-material'
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  ListItemAvatar,
  Avatar,
} from '@mui/material'
import React, { useCallback, useMemo, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { DurationTestStatus, Status } from '@/api/definition'
import { setDurationTestStatus } from '@/api/Device'
import { layoutStyles } from '@/utils/theme'
import useResponseValidator from '@/pages/utils/responseValidator'
import SelectDevicesForDurationTestStep from './SelectDevicesForDurationTestStep'
import {
  SelectedDevicesContext,
  initialSelectedDevicesState,
  selectedDevicesReducer,
} from './SelectedDevicesContext'
import Concerns from '@/pages/Concerns'

interface StartDurationTestStepProps {
  [key: string]: unknown
}

const StartDurationTestStep: React.FC<StartDurationTestStepProps> = () => {
  const { t } = useTranslation(['app'])
  const { showTransferError } = useResponseValidator()
  const [selectedDevicesState, selectedDevicesDispatch] = useReducer(
    selectedDevicesReducer,
    initialSelectedDevicesState,
  )

  const handleStartDurationTest = useCallback(async () => {
    if (selectedDevicesState.selectedDevices.length === 0) {
      selectedDevicesDispatch({
        type: 'SET_ERROR',
        payload: 'No devices selected',
      })
      return
    }
    selectedDevicesDispatch({ type: 'SET_LOADING', payload: true })
    selectedDevicesDispatch({ type: 'SET_ERROR', payload: null })
    selectedDevicesDispatch({
      type: 'SET_ACTION',
      payload: DurationTestStatus.DurationTestInProgress,
    })
    try {
      const { startDurationTest } = await import('@/api/Device')
      selectedDevicesDispatch({ type: 'SET_ACTIVE_STEP', payload: 2 })

      for (const device of selectedDevicesState.selectedDevices) {
        selectedDevicesDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: { uid: device.uid, status: { status: 'loading' as const } },
        })

        const results = await startDurationTest([device.uid])
        const result = results[0]

        if (result.status === Status.SUCCESS) {
          selectedDevicesDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: { status: 'success' as const },
            },
          })
        } else {
          // Show HTTP error in snackbar
          showTransferError(result)

          selectedDevicesDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: {
                status: 'error' as const,
                message: result.reason || 'Failed to start duration test',
              },
            },
          })
        }
      }
    } catch (error) {
      selectedDevicesDispatch({
        type: 'SET_ERROR',
        payload:
          error instanceof Error
            ? error.message
            : 'Failed to start duration test',
      })
    } finally {
      selectedDevicesDispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [selectedDevicesState.selectedDevices, showTransferError])

  const handleSkipDurationTest = useCallback(async () => {
    if (selectedDevicesState.selectedDevices.length === 0) {
      selectedDevicesDispatch({
        type: 'SET_ERROR',
        payload: 'No devices selected',
      })
      return
    }
    selectedDevicesDispatch({ type: 'SET_LOADING', payload: true })
    selectedDevicesDispatch({ type: 'SET_ERROR', payload: null })
    selectedDevicesDispatch({
      type: 'SET_ACTION',
      payload: DurationTestStatus.DurationTestSkipped,
    })
    try {
      const { setDurationTestStatus } = await import('@/api/Device')
      selectedDevicesDispatch({ type: 'SET_ACTIVE_STEP', payload: 2 })

      for (const device of selectedDevicesState.selectedDevices) {
        selectedDevicesDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: { uid: device.uid, status: { status: 'loading' as const } },
        })

        const results = await setDurationTestStatus(
          [device.uid],
          DurationTestStatus.DurationTestSkipped,
        )
        const result = results[0]

        if (result.status === Status.SUCCESS) {
          selectedDevicesDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: { status: 'success' as const },
            },
          })
        } else {
          // Show HTTP error in snackbar
          showTransferError(result)

          selectedDevicesDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: {
                status: 'error' as const,
                message: result.reason || 'Failed to skip duration test',
              },
            },
          })
        }
      }
    } catch (error) {
      selectedDevicesDispatch({
        type: 'SET_ERROR',
        payload:
          error instanceof Error
            ? error.message
            : 'Failed to skip duration test',
      })
    } finally {
      selectedDevicesDispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [selectedDevicesState.selectedDevices, showTransferError])

  const handleBackToStep1 = useCallback(() => {
    selectedDevicesDispatch({ type: 'SET_ACTIVE_STEP', payload: 1 })
  }, [])

  const handleRetry = useCallback(
    async (uid: string) => {
      selectedDevicesDispatch({
        type: 'SET_REQUEST_STATUS',
        payload: { uid, status: { status: 'loading' } },
      })

      const action =
        selectedDevicesState.action ===
        DurationTestStatus.DurationTestInProgress
          ? DurationTestStatus.DurationTestInProgress
          : DurationTestStatus.DurationTestSkipped

      const results = await setDurationTestStatus([uid], action)
      const result = results[0]

      if (result.status === Status.SUCCESS) {
        selectedDevicesDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: { uid, status: { status: 'success' } },
        })
      } else {
        // Show HTTP error in snackbar
        showTransferError(result)

        selectedDevicesDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: {
            uid,
            status: {
              status: 'error' as const,
              message: result.reason || 'Failed to update status',
            },
          },
        })
      }
    },
    [selectedDevicesState.action, showTransferError],
  )

  const selectedDevicesContextValue = useMemo(
    () => ({
      state: selectedDevicesState,
      dispatch: selectedDevicesDispatch,
      onStartDurationTest: handleStartDurationTest,
      onSkipDurationTest: handleSkipDurationTest,
      onBackToStep1: handleBackToStep1,
    }),
    [
      selectedDevicesState,
      selectedDevicesDispatch,
      handleStartDurationTest,
      handleSkipDurationTest,
      handleBackToStep1,
    ],
  )

  const renderStep1 = () => (
    <>
      <SelectDevicesForDurationTestStep />
      {selectedDevicesState.error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {selectedDevicesState.error}
        </Typography>
      )}
    </>
  )

  const renderStep2 = () => (
    <Box>
      <List
        sx={{
          width: '100%',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {selectedDevicesState.selectedDevices.map((device) => {
          const request = selectedDevicesState.requestStatuses.get(device.uid)
          return (
            <React.Fragment key={device.uid}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  pr: 8,
                  position: 'relative',
                  borderBottom: '1px solid #eee',
                }}
              >
                <ListItemAvatar>
                  <Avatar>
                    <Devices />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={device.serial ?? device.uid}
                  secondary={`${device.type ?? 'Unknown Type'}${device.label ? ' - ' + device.label : ''}`}
                />
                {request && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {request.status === 'loading' && (
                      <CircularProgress size={24} />
                    )}
                    {request.status === 'success' && (
                      <CheckCircle color="success" />
                    )}
                    {request.status === 'error' && (
                      <IconButton
                        size="small"
                        onClick={() => void handleRetry(device.uid)}
                        color="error"
                        aria-label={t('common:general.retry')}
                        sx={{
                          minWidth: 0,
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          p: 0,
                          backgroundColor: 'takeoutofservice.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'takeoutofservice.dark',
                          },
                        }}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </ListItem>
              {/* Add Concerns component after each ListItem */}
              {request?.status === 'error' && request.message && (
                <Concerns message={t(request.message)} concerns={[]} />
              )}
            </React.Fragment>
          )
        })}
      </List>
      {selectedDevicesState.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {selectedDevicesState.error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleBackToStep1}
          disabled={selectedDevicesState.isLoading}
          sx={{
            marginTop: 2,
            marginRight: 2,
            color: '#fff',
          }}
        >
          {t('common:general.back')}
        </Button>
      </Box>
    </Box>
  )

  const steps = [
    t('device.durationTest.selectDevices'),
    t('device.durationTest.startSkipDurationTest'),
  ]

  return (
    <SelectedDevicesContext.Provider value={selectedDevicesContextValue}>
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Stepper
          activeStep={selectedDevicesState.activeStep - 1}
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box
          data-testid="box-1"
          sx={{ mt: 4, position: 'relative', overflow: 'hidden' }}
        >
          <Box
            sx={{
              transform: `translateX(-${(selectedDevicesState.activeStep - 1) * 100}%)`,
              transition: 'transform 0.3s ease-in-out',
              display: 'flex',
            }}
          >
            <Box sx={{ width: '100%', flexShrink: 0 }}>{renderStep1()}</Box>
            <Box sx={{ width: '100%', flexShrink: 0 }}>{renderStep2()}</Box>
          </Box>
        </Box>
      </Box>
    </SelectedDevicesContext.Provider>
  )
}

export default StartDurationTestStep
